import { segment } from '@actions/analytics';
import { CHECKOUT_CBT_USER_DETAILS_SUBMITTED } from '@cont/Checkout/constants/segment';
import { evtNameCategoryMap } from '@constants/index';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getFullName } from '@api/selectors/auth';
import { fetchCbtCentres, fetchCbtLocations } from '@api/fetchers/cbt';
import { useSnackbar } from '@stores/snackbar';
import { useEffect, useRef, useState } from 'react';
import Image from '@base/Image';
import useTestSeriesInfo from './useTestSeriesInfo';
import CbtCentreSelect from './CbtCentreSelect';
import CbtSearchableSelect from './CbtSearchableSelect';
import useCbtVenueDropdown from './useCbtVenueDropdown';
import MapsLink from './MapsLink';
import {
  ActionRow,
  BannerStack,
  BannerText,
  FeedbackBanner,
  FeedbackLink,
  FieldGroup,
  FieldLabel,
  FieldLabelRow,
  FormWrapper,
  HelperText,
  NameFieldWrapper,
  ProceedButton,
  StyledTextField,
  WarningBanner,
  WarningText
} from './CbtVenueSelectionForm.styles';

const CbtVenueSelectionForm = ({
  programmeUid,
  onProceed,
  onVenueChange,
  getSegmentProps,
  isMobile = false,
  hideProceedButton = false,
  isEditMode = false,
  onOpenPreferredLocation,
  remainingCentreChanges,
  canChangeCentre,
  prefill
}) => {
  const meData = useMyInfo(getFullName);
  const { open: showSnackbar } = useSnackbar();
  const { data: testSeriesData } = useTestSeriesInfo();
  const [fullName, setFullName] = useState('');
  const [openDropdown, setOpenDropdown] = useState(null);

  const resolvedProgrammeUid = programmeUid ?? testSeriesData?.uid;

  const [locations, setLocations] = useState({});
  const [isLocationsLoading, setIsLocationsLoading] = useState(false);
  const [isLocationsError, setIsLocationsError] = useState(false);

  const [centres, setCentres] = useState([]);
  const [isCentresLoading, setIsCentresLoading] = useState(false);
  const [isCentresError, setIsCentresError] = useState(false);

  useEffect(() => {
    if (!resolvedProgrammeUid) return undefined;

    let cancelled = false;

    setIsLocationsLoading(true);
    setIsLocationsError(false);
    setLocations({});

    fetchCbtLocations(resolvedProgrammeUid)
      .then((response) => {
        if (cancelled) return;
        if (response?.error) {
          setIsLocationsError(true);
          return;
        }
        setLocations(response?.locations ?? {});
      })
      .catch(() => {
        if (cancelled) return;
        setIsLocationsError(true);
      })
      .finally(() => {
        if (!cancelled) setIsLocationsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [resolvedProgrammeUid]);

  useEffect(() => {
    if (meData) {
      setFullName(meData);
    }
  }, [meData]);

  const {
    stateOptions,
    cityOptions,
    centerOptions,
    selectedState,
    selectedCity,
    selectedCenter,
    setSelectedState,
    setSelectedCity,
    setSelectedCenter
  } = useCbtVenueDropdown({ locations, centres });

  useEffect(() => {
    if (!resolvedProgrammeUid || !selectedCity?.value) {
      setCentres([]);
      return undefined;
    }

    let cancelled = false;
    const city = selectedCity.value;

    setIsCentresLoading(true);
    setIsCentresError(false);
    setCentres([]);

    fetchCbtCentres(resolvedProgrammeUid, city)
      .then((response) => {
        if (cancelled) return;
        if (response?.error) {
          setIsCentresError(true);
          return;
        }
        setCentres(response?.centres ?? []);
      })
      .catch(() => {
        if (cancelled) return;
        setIsCentresError(true);
      })
      .finally(() => {
        if (!cancelled) setIsCentresLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [resolvedProgrammeUid, selectedCity?.value]);

  const prefilledRef = useRef({ state: false, city: false, centre: false });

  useEffect(() => {
    if (!isEditMode || !prefill) return;
    const done = prefilledRef.current;

    if (!done.state) {
      const stateMatch = stateOptions.find((o) => o.value === prefill.state);
      if (!stateMatch) return;
      done.state = true;
      setSelectedState(stateMatch);
      return;
    }

    if (!done.city) {
      const cityMatch = cityOptions.find((o) => o.value === prefill.city);
      if (!cityMatch) return;
      done.city = true;
      setSelectedCity(cityMatch);
      return;
    }

    if (!done.centre) {
      const prefillCentreName = prefill.centreName?.trim().toLowerCase();
      const centreMatch = prefill.centreUid
        ? centerOptions.find((o) => o.value === prefill.centreUid)
        : centerOptions.find((option) => {
            const centre = centres.find((c) => c.centreUid === option.value);
            return (
              !!prefillCentreName &&
              centre?.centreName?.trim().toLowerCase() === prefillCentreName
            );
          });
      if (!centreMatch) return;
      done.centre = true;
      setSelectedCenter(centreMatch);
    }
  }, [
    isEditMode,
    prefill,
    stateOptions,
    cityOptions,
    centerOptions,
    centres,
    setSelectedState,
    setSelectedCity,
    setSelectedCenter
  ]);

  const selectedCentreData = selectedCenter
    ? centres.find((centre) => centre.centreUid === selectedCenter.value)
    : null;

  const [isSubmitting, setIsSubmitting] = useState(false);

  const canProceed =
    fullName.trim().length > 0 &&
    selectedState &&
    selectedCity &&
    selectedCenter &&
    selectedCentreData?.availableSeats > 0 &&
    !isCentresLoading;

  useEffect(() => {
    if (!onVenueChange) return;
    onVenueChange(
      canProceed && selectedCentreData
        ? {
            fullName: fullName.trim(),
            state: selectedState.value,
            city: selectedCity.value,
            centreUid: selectedCentreData.centreUid,
            centreName: selectedCentreData.centreName,
            centreAddress: selectedCentreData.centreAddress
          }
        : null
    );
  }, [
    canProceed,
    fullName,
    onVenueChange,
    selectedCentreData,
    selectedCity,
    selectedState
  ]);

  const handleDisabledNameClick = () => {
    if (!isEditMode) return;
    showSnackbar({
      message: 'Your name cannot be edited',
      type: 'info'
    });
  };

  const handleProceed = async () => {
    if (!canProceed || !selectedCentreData || isSubmitting) return;

    const venueSelection = {
      fullName: fullName.trim(),
      state: selectedState.value,
      city: selectedCity.value,
      centreUid: selectedCentreData.centreUid,
      centreName: selectedCentreData.centreName,
      centreAddress: selectedCentreData.centreAddress
    };

    if (getSegmentProps) {
      segment.track(
        CHECKOUT_CBT_USER_DETAILS_SUBMITTED,
        {
          ...getSegmentProps(venueSelection),
          is_change_centre: Boolean(isEditMode)
        },
        evtNameCategoryMap.BOTH
      );
    }

    setIsSubmitting(true);
    try {
      await onProceed(venueSelection);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getCentreChangeWarningMessage = (remaining) => {
    if (remaining <= 0) return 'You have exhausted centre change limit.';
    if (remaining === 1) return 'You can only edit your test centre once.';
    return 'You can only edit your test centre twice.';
  };

  return (
    <FormWrapper>
      <FieldGroup>
        <FieldLabel variant="p3">Full name</FieldLabel>
        <NameFieldWrapper
          $isDisabled={isEditMode}
          onClick={handleDisabledNameClick}
        >
          <StyledTextField
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            placeholder="Enter your full name"
            width="100%"
            disabled={isEditMode}
          />
        </NameFieldWrapper>
        <HelperText variant="p3">
          Enter your full legal name as per your Aadhar card
        </HelperText>
      </FieldGroup>

      <FieldGroup>
        <FieldLabel variant="p3">Choice of state</FieldLabel>
        <CbtSearchableSelect
          options={stateOptions}
          selectedOption={selectedState}
          onSelect={setSelectedState}
          placeholder="Search for state"
          disabled={isLocationsLoading || !stateOptions.length}
          isOpen={openDropdown === 'state'}
          onToggle={(isOpen) => setOpenDropdown(isOpen ? 'state' : null)}
        />
        {isLocationsError && (
          <HelperText variant="p3">
            Unable to load locations. Please go back and try again.
          </HelperText>
        )}
        {!isLocationsLoading && !isLocationsError && !stateOptions.length && (
          <HelperText variant="p3">
            No locations available for this test series.
          </HelperText>
        )}
      </FieldGroup>

      <FieldGroup>
        <FieldLabel variant="p3">Choice of city</FieldLabel>
        <CbtSearchableSelect
          options={cityOptions}
          selectedOption={selectedCity}
          onSelect={setSelectedCity}
          placeholder="Search for city"
          disabled={!selectedState}
          isOpen={openDropdown === 'city'}
          onToggle={(isOpen) => setOpenDropdown(isOpen ? 'city' : null)}
        />
      </FieldGroup>

      <FieldGroup>
        <FieldLabelRow>
          <FieldLabel variant="p3">Choice of centre</FieldLabel>
          <MapsLink
            href={selectedCentreData?.centreGoogleMapsLink}
            marginTop="0"
            showDisabled
          />
        </FieldLabelRow>
        <CbtCentreSelect
          options={centerOptions}
          selectedOption={selectedCenter}
          onSelect={setSelectedCenter}
          disabled={!selectedCity || isCentresLoading}
          isOpen={openDropdown === 'centre'}
          onToggle={(isOpen) => setOpenDropdown(isOpen ? 'centre' : null)}
        />
        {isCentresError && (
          <HelperText variant="p3">
            Unable to load centres. Please try another city.
          </HelperText>
        )}
      </FieldGroup>

      <BannerStack>
        {isEditMode && (
          <WarningBanner>
            <Image
              src="offline-centres/test-centre-count.svg"
              width={16}
              height={16}
              alt=""
            />
            <WarningText variant="p2">
              {getCentreChangeWarningMessage(remainingCentreChanges)}
            </WarningText>
          </WarningBanner>
        )}
        <WarningBanner>
          <Image
            src="offline-centres/test-centre-count.svg"
            width={16}
            height={16}
            alt=""
          />
          <WarningText variant="p2">
            Admit card will be available soon. generated.
          </WarningText>
        </WarningBanner>
        <FeedbackBanner>
          <BannerText variant="p2">
            Please give us feedback on your preferred location for the test{' '}
            <FeedbackLink
              type="button"
              onClick={() => onOpenPreferredLocation?.()}
            >
              here
            </FeedbackLink>
          </BannerText>
        </FeedbackBanner>
      </BannerStack>

      {!hideProceedButton && (
        <ActionRow>
          <ProceedButton
            label={isEditMode ? 'Submit' : 'Proceed to buy test series'}
            variant="primary"
            size={isMobile ? 'large' : 'medium'}
            disabled={
              !canProceed ||
              isSubmitting ||
              (isEditMode &&
                (!(remainingCentreChanges > 0) || !canChangeCentre))
            }
            onClick={handleProceed}
          />
        </ActionRow>
      )}
    </FormWrapper>
  );
};

export default CbtVenueSelectionForm;
